import { useState } from 'react';
import { apiPost, usePurchaseApi } from './purchaseApi';
import { getErrorMessage } from '@/services/api';

/**
 * Board #571: the shared KYC step's first screen. The server (`proceed` -> next_step
 * `kyc_start`) says what it still needs; this posts the customer's consent (and a PAN / date of
 * birth only if the server has none on file) to `/kyc-check/`, then asks `proceed` again, which
 * answers `kyc` (go to the insurer's page), `wait` or `pay`.
 */
export interface KycBlock {
  status: string;
  message?: string;
  needs?: string[];
  consent_text?: string;
  pan_masked?: string;
  required_documents?: string[];
}

const PAN_RE = /^[A-Za-z]{5}[0-9]{4}[A-Za-z]$/;
const inputClass =
  'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500';
const primaryButton =
  'px-5 py-2.5 rounded-lg bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed';

export function KycStartForm({
  token,
  insurerName,
  message,
  kyc,
  onStarted,
}: {
  token: string;
  insurerName: string;
  message: string;
  kyc: KycBlock;
  onStarted: () => void;
}) {
  const api = usePurchaseApi();
  const needs = kyc.needs ?? [];
  const [consent, setConsent] = useState(false);
  const [pan, setPan] = useState('');
  const [dob, setDob] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const panInvalid = needs.includes('pan_number') && !PAN_RE.test(pan.trim());
  const dobMissing = needs.includes('dob') && !dob;
  const canSubmit = consent && !panInvalid && !dobMissing && !busy;

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await apiPost(api, `${api.base(token)}/kyc-check/`, {
        consent,
        ...(needs.includes('pan_number') ? { pan_number: pan.trim().toUpperCase() } : {}),
        ...(needs.includes('dob') ? { dob } : {}),
      });
      onStarted();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      className="space-y-3"
      aria-label="Verify your KYC"
      onSubmit={(e) => {
        e.preventDefault();
        if (canSubmit) void submit();
      }}
    >
      <p className="text-sm text-gray-700 dark:text-gray-300">{message}</p>
      {needs.includes('pan_number') && (
        <div>
          <label htmlFor="kyc-pan" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
            PAN
          </label>
          <input
            id="kyc-pan"
            className={inputClass}
            value={pan}
            maxLength={10}
            autoComplete="off"
            onChange={(e) => setPan(e.target.value.toUpperCase())}
          />
        </div>
      )}
      {needs.includes('dob') && (
        <div>
          <label htmlFor="kyc-dob" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
            Date of birth
          </label>
          <input id="kyc-dob" type="date" className={inputClass} value={dob} onChange={(e) => setDob(e.target.value)} />
        </div>
      )}
      <label className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
        <input
          type="checkbox"
          className="mt-1"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
        />
        <span>{kyc.consent_text || `I agree that my details may be sent to ${insurerName} to verify my KYC.`}</span>
      </label>
      {error && (
        <p className="text-sm text-red-600" role="alert">
          {error}
        </p>
      )}
      <button type="submit" disabled={!canSubmit} className={primaryButton}>
        {busy ? 'Please wait…' : 'Verify my KYC'}
      </button>
    </form>
  );
}
